<script setup lang="ts">
import { ActiviteitItem, BekostigingsType } from "@/repositories/activiteit.repository";
import { computed } from "vue";
import { Tooltip } from "floating-vue";
import { formatCurrency } from "@/helpers/currency.helper";
import { useSportactiviteitBestelStore } from "@/stores/sportactiviteit-bestel.store";
import { SportactiviteitKosten } from "@/repositories/bestel.repository";

const props = defineProps<{
    activiteit: ActiviteitItem | null;
    boeking: {
        Jezelf: { Include: boolean };
        Volwassenen: unknown[];
        Kinderen: unknown[];
        Deelnemers: number;
        Begeleiders: number;
        Groepen: Array<{ Deelnemers: number; Begeleiders: number; OrganisatieNaam: string }>;
    };
    alleKosten: SportactiviteitKosten | null;
}>();

const bekostigingstype = computed(() => props.activiteit?.Bekostigingstype);
const kortingBedrag = computed(() => props.alleKosten?.Korting ?? 0);
const store = useSportactiviteitBestelStore();

const kostenPerBoeking = computed(() => {
    return props.alleKosten?.KostenPerInschrijving ?? props.activiteit?.Kosten ?? 0;
});

const serviceKostenPerBoeking = computed(() => {
    return props.alleKosten?.ServiceKosten ?? props.activiteit?.ServiceKosten ?? 0;
});

const prijsLabel = (count: number) => {
    if (bekostigingstype.value === BekostigingsType.CREDITS) {
        return `${count} ${count > 1 ? "credits" : "credit"}`;
    }

    if (bekostigingstype.value === BekostigingsType.GRATIS) {
        return "Gratis";
    }

    if (bekostigingstype.value === BekostigingsType.BETALING) {
        return formatCurrency(kostenPerBoeking.value);
    }

    if (bekostigingstype.value === BekostigingsType.ABONNEMENT) {
        return `${formatCurrency(kostenPerBoeking.value)} per ${store.abonnementPeriode}`;
    }

    return "";
};

const showServiceKosten = computed(() => {
    const isBetaaldType = bekostigingstype.value === BekostigingsType.BETALING
        || bekostigingstype.value === BekostigingsType.ABONNEMENT;

    return isBetaaldType
        && serviceKostenPerBoeking.value > 0
        && props.alleKosten?.AantalInschrijvingen != undefined && props.alleKosten?.AantalInschrijvingen > 0;
});

const totalPrice = computed(() => {
    if (!props.activiteit) {
        return "";
    }

    if (bekostigingstype.value === BekostigingsType.GRATIS) {
        return "GRATIS";
    }

    if (bekostigingstype.value === BekostigingsType.OPLOCATIE) {
        return `${formatCurrency(props.alleKosten?.TotaalKosten ?? 0)} op locatie`;
    }

    if (bekostigingstype.value === BekostigingsType.CREDITS) {
        return props.alleKosten?.AantalInschrijvingen;
    }

    if (bekostigingstype.value === BekostigingsType.ABONNEMENT) {
        return `${formatCurrency(props.alleKosten?.TotaalKosten ?? 0)} per ${store.abonnementPeriode}`;
    }

    return formatCurrency(props.alleKosten?.TotaalKosten ?? 0);
});
</script>

<template>
    <div>
        <hr class="ticket-divider my-1 my-md-2">
        <p class="payment-cart-info-box-price-overview-title">Prijsoverzicht</p>
        <ul class="list-unstyled payment-cart-info-box-price-list font-size-sm">
            <li v-if="boeking.Jezelf.Include">
                1x jezelf
                <span class="float-right">{{ prijsLabel(1) }}</span>
            </li>
            <li v-if="boeking.Volwassenen.length">
                {{ boeking.Volwassenen.length }}x vriend(in)
                <span class="float-right">{{ prijsLabel(boeking.Volwassenen.length) }}</span>
            </li>
            <li v-if="boeking.Kinderen.length">
                {{ boeking.Kinderen.length }}x kind(eren)
                <span class="float-right">{{ prijsLabel(boeking.Kinderen.length) }}</span>
            </li>
            <template v-if="activiteit?.Kostentype !== 1">
                <li v-for="(group, index) in boeking.Groepen" :key="index"
                    v-show="group.Begeleiders > 0 || group.Deelnemers > 0">
                    <span class="font-weight-bold mt-1">
                        {{ group.OrganisatieNaam !== "" ? group.OrganisatieNaam : "Groep " + (index + 1) }}</span>
                    <div>
                        {{ group.Deelnemers }}x {{ group.Deelnemers > 1 ? "deelnemers" : "deelnemer" }}
                        <span class="float-right">{{ prijsLabel(group.Deelnemers) }}</span>
                    </div>
                    <div>
                        {{ group.Begeleiders }}x {{ group.Begeleiders > 1 ? "begeleiders" : "begeleider" }}
                        <span class="float-right">{{ prijsLabel(group.Begeleiders) }}</span>
                    </div>
                </li>

            </template>

            <template v-if="activiteit?.Kostentype === 1">
                <li v-for="(group, index) in boeking.Groepen" :key="index">
                    <div>
                        <span class="font-weight-bold mt-1">
                            {{ group.OrganisatieNaam ? group.OrganisatieNaam : "Groep " + (index + 1) }}
                        </span>
                        <span class="float-right">{{ prijsLabel(alleKosten?.KostenPerInschrijving ?? 0) }}</span>
                    </div>
                </li>
            </template>

            <li v-if="boeking.Deelnemers > 0">
                {{ boeking.Deelnemers }}x {{ boeking.Deelnemers > 1 ? "deelnemers" : "deelnemer" }}
                <span class="float-right">{{ prijsLabel(boeking.Deelnemers) }}</span>
            </li>
            <li v-if="boeking.Begeleiders > 0">
                {{ boeking.Begeleiders }}x {{ boeking.Begeleiders > 1 ? "begeleiders" : "begeleider" }}
                <span class="float-right">{{ prijsLabel(boeking.Begeleiders) }}</span>
            </li>
            <li v-if="showServiceKosten">
                {{ alleKosten?.AantalInschrijvingen }}x servicekosten <span class="float-right">{{
                    formatCurrency(serviceKostenPerBoeking) }}</span>
            </li>
            <li class="d-flex justify-content-between align-items-center" v-if="activiteit?.ExtraKosten">
                Extra kosten
                <Tooltip :triggers="['click', 'hover']" :theme="'payment-info'">
                    <span class="icon-info" aria-hidden="true"></span>
                    <template #popper>
                        Er zijn mogelijk extra kosten verbonden. Denk bijv. aan materiaalhuur. Deze kosten dienen op
                        locatie te worden voldaan.
                    </template>
                </Tooltip>
            </li>
        </ul>

        <div v-if="kortingBedrag > 0">
            <hr class="dashed my-1">
            <ul class="list-unstyled text-muted mb-0">
                <li>Kortingscode <span class="float-right">- {{ formatCurrency(kortingBedrag) }}</span></li>
                <li v-if="store.validActiecode?.Meta">{{ store.validActiecode.Meta }}</li>
            </ul>
        </div>

        <hr class="dashed total-price-divder">

        <div class="payment-cart-info-box-price-box">
            <p class="payment-cart-info-box-price-box-totaal">Totaal</p>
            <p class="payment-cart-info-box-price-box-price">{{ totalPrice }}</p>
        </div>

        <div v-if="activiteit?.ExtraKosten">
            <hr class="ticket-divider my-1 my-md-2">

            <div class="font-weight-bold">
                <span class="icon icon-alert-circle text-primary mr-1 font-weight-bold"></span> Extra kosten
            </div>
            <span>
                {{ activiteit?.ExtraKostenBeschrijving }}
            </span>
        </div>
    </div>
</template>
